import { useMutation, useQuery } from "@apollo/client";
import { Form, Formik } from "formik";
import gql from "graphql-tag";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { toastSuccessOptions } from "../../../App";
import { handleGraphQLErrors } from "../../../utils/handleGraphQLError";
import { Button } from "../../SharedComponents/Button/Button";
import {
  EditableInformation,
  EditableInformationItemWrapper,
  EditableInformationTextInput,
} from "../../SharedComponents/EditableInformation/EditableInformation";
import { EditHeader } from "../../SharedComponents/EditHeader/EditHeader";
import "./AdminCountryEdit.css";

export const AdminCountryEdit = () => {
  const { t } = useTranslation();
  /** @type {*} */
  const params = useParams();
  const history = useHistory();

  const { data, error, loading } = useQuery(getCountryQuery, {
    variables: { id: params?.id },
  });

  const [updateCountry] = useMutation(updateCountryMutation);

  useEffect(() => {
    if (error?.message) {
      handleGraphQLErrors(error);
    }
  }, [error]);

  if (loading) return <div>Im loading...</div>;

  return (
    <div className="AdminCountryEdit">
      <Formik
        initialValues={{
          id: data?.country?.id || "",
          name: data?.country?.name || "",
          code: data?.country?.code || "",
        }}
        onSubmit={(updatedData) => {
          return updateCountry({
            variables: {
              id: updatedData.id,
              name: updatedData.name,
              code: updatedData.code,
            },
          })
            .then((result) => {
              toast(
                t("toasts.updatedCountry") + result?.data?.updateCountry?.name,
                toastSuccessOptions
              );
              history.goBack();
            })
            .catch((err) => {
              handleGraphQLErrors(err);
            });
        }}
        validationSchema={validationSchema}
      >
        {({ isValid, dirty }) => (
          <Form style={{ display: "flex", width: "100%", height: "100%" }}>
            <div className="AdminDistributorEdit__content">
              <EditHeader
                type="types.country"
                name="country.edit"
                status={null}
                goBack={() => history.goBack()}
              />
              <div className="AdminDistributorEdit__companyinfo">
                <EditableInformation title="Country information">
                  <>
                    <EditableInformationItemWrapper label="country.name">
                      <EditableInformationTextInput
                        name="name"
                        type="text"
                        defaultEditingState={true}
                      />
                    </EditableInformationItemWrapper>
                    <EditableInformationItemWrapper label="country.code">
                      <EditableInformationTextInput
                        name="code"
                        type="text"
                        defaultEditingState={true}
                      />
                    </EditableInformationItemWrapper>
                  </>
                </EditableInformation>
              </div>
            </div>
            <div className="AdminDistributorEdit__right">
              <div className="AdminDistributorEdit__right--actions">
                <Button
                  buttonStyle="Button__primary"
                  disabled={!isValid || !dirty}
                  type="submit"
                >
                  {t("generic.save")}
                </Button>
                <Button
                  buttonStyle="Button__secondary"
                  click={(e) => {
                    e.preventDefault();
                    history.goBack();
                  }}
                >
                  {t("generic.cancel")}
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

const getCountryQuery = gql`
  query country($id: String) {
    country(id: $id) {
      id
      name
      code
    }
  }
`;

const updateCountryMutation = gql`
  mutation updateCountry($id: String, $name: String, $code: String) {
    updateCountry(id: $id, name: $name, code: $code) {
      id
      name
      code
    }
  }
`;

const validationSchema = Yup.object().shape({
  name: Yup.string().required("You must enter a name"),
  code: Yup.string(),
});
